import { Link } from '@tanstack/react-router';
import { AlertTriangle, CreditCard } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { useOrganizationBilling } from '@/features/organizations/hooks/useOrganizationBilling';
import { useOrganizationPlatformAccess } from '@/features/organizations/hooks/useOrganizationPlatformAccess';
import { useCurrentOrganization } from '@/features/organizations/hooks/useCurrentOrganization';
import { canManageOrganizationBilling } from '@/features/organizations/permissions';
import { useOrganizationPublishingLimits, usePublishingTargets } from '@/features/publishing/hooks/usePublishing';
import { PUBLISHING_POSTS_PAID_PLAN_LIMIT_FEATURE_KEY } from '@/features/publishing/services/publishing';
import { useOrganizationSocialAccountSourceLimit } from '@/features/social-accounts/hooks/useSocialAccounts';
import { cn } from '@/lib/utils';
import { isTrialEndingSoonNow, resolvePublishingLimitBanner, type BannerConfig } from './organization-limit-banner.utils';

const LIMIT_WARNING_THRESHOLD = 0.8;

export function OrganizationLimitBanner() {
    const { t } = useTranslation();
    const { currentOrganization, currentOrganizationId, isPending: isOrganizationPending } = useCurrentOrganization();
    const billingQuery = useOrganizationBilling(currentOrganizationId);
    const platformAccess = useOrganizationPlatformAccess(
        currentOrganizationId,
        currentOrganization?.hasActivePlatformAccess,
    );
    const sourceLimitQuery = useOrganizationSocialAccountSourceLimit(currentOrganizationId);
    const publishingLimitsQuery = useOrganizationPublishingLimits(
        currentOrganizationId,
        PUBLISHING_POSTS_PAID_PLAN_LIMIT_FEATURE_KEY,
    );
    const publishingTargetsQuery = usePublishingTargets(currentOrganizationId);

    const canManageBilling = canManageOrganizationBilling(currentOrganization?.membership?.role);

    const banner = useMemo<BannerConfig | null>(() => {
        if (!currentOrganizationId || isOrganizationPending) {
            return null;
        }

        if (platformAccess.isInactive) {
            const billing = billingQuery.data;
            const isCanceled = billing?.cancelAtPeriodEnd && billing.currentPeriodEnd;

            if (isCanceled) {
                return {
                    tone: 'critical',
                    title: t('organizations.limitBanner.cancelEndingTitle', {
                        defaultValue: 'Subscription ending soon',
                    }),
                    description: t('organizations.limitBanner.cancelEndingDescription', {
                        defaultValue:
                            'This workspace is set to lose access at the end of the current billing period. Renew or update billing to avoid interruption.',
                    }),
                    actionLabel: t('organizations.limitBanner.manageBillingAction', {
                        defaultValue: 'Manage billing',
                    }),
                };
            }

            return {
                tone: 'critical',
                title: t('organizations.subscriptionInactive.title'),
                description: t('organizations.limitBanner.subscriptionInactiveDescription', {
                    defaultValue: 'This workspace has inactive billing access. Update the subscription to restore full access.',
                }),
                actionLabel: t('organizations.subscriptionInactive.manageBillingAction'),
            };
        }

        const billing = billingQuery.data;
        if (isTrialEndingSoonNow(billing?.trialEndsAt)) {
            return {
                tone: 'warning',
                title: t('organizations.limitBanner.trialEndingTitle', {
                    defaultValue: 'Trial ending soon',
                }),
                description: t('organizations.limitBanner.trialEndingDescription', {
                    defaultValue: 'Your trial is close to ending. Add billing details or upgrade now to avoid interruption.',
                }),
                actionLabel: t('organizations.limitBanner.manageBillingAction', {
                    defaultValue: 'Manage billing',
                }),
            };
        }

        const sourceLimit = sourceLimitQuery.data;
        if (sourceLimit && typeof sourceLimit.limit === 'number' && sourceLimit.limit > 0) {
            const sourceUsageRatio = sourceLimit.used / sourceLimit.limit;

            if (!sourceLimit.canConnect || sourceUsageRatio >= 1) {
                return {
                    tone: 'critical',
                    title: t('organizations.limitBanner.socialAccountsReachedTitle', {
                        defaultValue: 'Social account limit reached',
                    }),
                    description: t('organizations.limitBanner.socialAccountsReachedDescription', {
                        defaultValue:
                            'You are using {{used}} of {{limit}} connected social account slots. Upgrade to connect more accounts.',
                        used: sourceLimit.used,
                        limit: sourceLimit.limit,
                    }),
                    actionLabel: t('organizations.limitBanner.upgradeAction', {
                        defaultValue: 'Upgrade plan',
                    }),
                };
            }

            if (sourceUsageRatio >= LIMIT_WARNING_THRESHOLD) {
                return {
                    tone: 'warning',
                    title: t('organizations.limitBanner.socialAccountsWarningTitle', {
                        defaultValue: 'Approaching social account limit',
                    }),
                    description: t('organizations.limitBanner.socialAccountsWarningDescription', {
                        defaultValue:
                            'You are using {{used}} of {{limit}} connected social account slots. Upgrade before new connections get blocked.',
                        used: sourceLimit.used,
                        limit: sourceLimit.limit,
                    }),
                    actionLabel: t('organizations.limitBanner.upgradeAction', {
                        defaultValue: 'Upgrade plan',
                    }),
                };
            }
        }

        const targetNameById = new Map(
            (publishingTargetsQuery.data ?? []).map((target) => [target.id, target.displayName] as const),
        );

        return resolvePublishingLimitBanner(publishingLimitsQuery.data ?? [], targetNameById, t);
    }, [
        billingQuery.data,
        currentOrganizationId,
        isOrganizationPending,
        platformAccess.isInactive,
        publishingLimitsQuery.data,
        publishingTargetsQuery.data,
        sourceLimitQuery.data,
        t,
    ]);

    if (!banner) {
        return null;
    }

    return (
        <div className='border-b bg-background/95 px-4 py-3 backdrop-blur sm:px-6'>
            <div
                role='alert'
                className={cn(
                    'mx-auto flex w-full max-w-6xl flex-col gap-3 rounded-xl border p-4 text-sm md:flex-row md:items-start md:justify-between',
                    banner.tone === 'critical'
                        ? 'border-destructive/30 bg-destructive/5 text-destructive'
                        : 'border-amber-500/30 bg-amber-500/10 text-amber-950 dark:text-amber-200',
                )}
            >
                <div className='flex items-start gap-3'>
                    {banner.tone === 'critical' ? (
                        <CreditCard className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
                    ) : (
                        <AlertTriangle className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
                    )}
                    <div className='space-y-1'>
                        <p className='font-medium text-foreground'>{banner.title}</p>
                        <p className={banner.tone === 'critical' ? 'text-destructive/80' : 'text-current/80'}>
                            {banner.description}
                        </p>
                    </div>
                </div>

                {canManageBilling ? (
                    <Button type='button' size='sm' variant={banner.tone === 'critical' ? 'default' : 'outline'} asChild>
                        <Link to='/settings'>{banner.actionLabel}</Link>
                    </Button>
                ) : null}
            </div>
        </div>
    );
}
